import { useCallback, useEffect, useMemo, useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import { shopModeApi, type FloorBouquetInStock } from "../../api/shopMode";
import type { BelowFloorFlag, CustomerOrder, StaffOrderPaymentMethod } from "../../api/types";
import {
  NO_PAYMENT_CHOICE,
  readPaymentChoice,
  type PaymentChoice,
} from "../../utils/paymentSplit";
import { ShopFloorPaymentSplit, paymentProblemText } from "./ShopFloorPaymentSplit";
import { useT } from "../../i18n/LanguageProvider";
import { ShopFloorOrderCreate } from "./ShopFloorOrderCreate";
import { SaleCompleteFlow } from "./SaleCompleteFlow";
import { ShopFloorCollectExtras } from "./ShopFloorCollectExtras";
import { BelowFloorPrompt } from "./BelowFloorPrompt";
import { belowFloorWarnings } from "./kioskCostDisplay";
import { orderBouquetFloorLines } from "./collectExtrasRules";
import {
  cartBouquetFloorLines,
  cartToSaleLines,
  ticketLineProblem,
  useTicketCart,
} from "./ticketCart";
import { useBoxPickPrompt } from "./useBoxPickPrompt";
import {
  compareOrdersForDisplay,
  fulfillmentLabel,
  orderDayDate,
  orderDayKey,
  orderLinesSummary,
  orderSlotLabel,
  orderStatusClass,
  orderStatusLabel,
} from "../orders/orderUi";
import { OrderReferencePhotos } from "../orders/OrderReferencePhotos";
import { dateText, money } from "../../i18n/intl";

/** G-156 / G-177: the sale a counter handover wrote, and its lines below the floor. */
interface CollectedSale {
  saleId: string;
  belowFloor: BelowFloorFlag[];
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/**
 * Floor agenda of scheduled customer orders: a 7-day strip (today first) with
 * per-day counts, plus time-sorted cards for the selected day. Today's and
 * tomorrow's orders can be worked (OD-96/97: build tonight for the morning,
 * hand a Ready order over a day early); later days are read-only — the
 * backend enforces the same rule.
 */
export function ShopFloorOrdersPanel({
  onBuildOrder,
}: {
  onBuildOrder?: (order: CustomerOrder) => void;
}) {
  const { t } = useT();
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [selectedDayKey, setSelectedDayKey] = useState(() => orderDayKey(new Date()));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmingJobId, setConfirmingJobId] = useState<string | null>(null);
  /**
   * G-156 (OD-34, "all sales"): a counter handover is a sale too, so it gets
   * the receipt question. Held HERE, not in the row — the completed order
   * leaves the list on refresh and its row with it. No earnings card: the
   * handover response carries no commission (OD-251).
   */
  const [collected, setCollected] = useState<CollectedSale | null>(null);
  // G-177 (KI-132): the till's sequence — receipt question first, then the
  // florist is asked why each line went below its floor.
  const [belowFloorFlags, setBelowFloorFlags] = useState<BelowFloorFlag[]>([]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await shopModeApi.floorListUpcomingOrders();
      setOrders(list.sort(compareOrdersForDisplay));
    } catch (err: unknown) {
      setError(errorText(err, "orders.couldNotLoad"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // G-21 amendment (OD-28): the florist's "picked up by courier" confirmation
  // — the shop's witness of the handover, cross-checking the courier's tap.
  // G-177 (KI-89): the delivery must be able to write the sale, so a box
  // question is asked here, while the courier is still in the shop.
  const pickupBoxPrompt = useBoxPickPrompt();
  const confirmPickup = async (jobId: string) => {
    setConfirmingJobId(jobId);
    setError(null);
    try {
      await pickupBoxPrompt.run((boxAnswers) =>
        shopModeApi.floorConfirmPickup(jobId, boxAnswers),
      );
      await refresh();
    } catch (err: unknown) {
      setError(
        errorText(err, "orders.couldNotConfirmPickup"),
      );
    } finally {
      setConfirmingJobId(null);
    }
  };

  const days = useMemo(() => {
    const today = new Date();
    return Array.from({ length: 7 }, (_, i) => {
      const date = addDays(today, i);
      return { date, key: orderDayKey(date) };
    });
  }, []);

  const ordersByDay = useMemo(() => {
    const map = new Map<string, CustomerOrder[]>();
    for (const order of orders) {
      const key = orderDayKey(orderDayDate(order));
      const list = map.get(key) ?? [];
      list.push(order);
      map.set(key, list);
    }
    return map;
  }, [orders]);

  const todayKey = orderDayKey(new Date());
  const tomorrowKey = orderDayKey(addDays(new Date(), 1));
  const selectedOrders = ordersByDay.get(selectedDayKey) ?? [];
  const selectedIsWorkable =
    selectedDayKey === todayKey || selectedDayKey === tomorrowKey;

  // G-46: take the business at the counter — the create flow replaces the
  // list while open (kiosk screens are single-purpose, no stacked modals).
  const [creating, setCreating] = useState(false);

  if (creating) {
    return (
      <div className="card shop-floor-tasks-card">
        <ShopFloorOrderCreate
          onClose={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false);
            await refresh();
          }}
        />
      </div>
    );
  }

  return (
    <div className="card shop-floor-tasks-card">
      {collected ? (
        <SaleCompleteFlow
          saleId={collected.saleId}
          commissionPercent={null}
          commissionEarnedEur="0.00"
          onDone={() => {
            setBelowFloorFlags(collected.belowFloor);
            setCollected(null);
          }}
        />
      ) : null}
      {belowFloorFlags.length > 0 ? (
        <BelowFloorPrompt flags={belowFloorFlags} onDone={() => setBelowFloorFlags([])} />
      ) : null}
      {pickupBoxPrompt.element}
      <div className="day-shifts-row-head">
        <h3 style={{ margin: 0 }}>{t("orders.toPrepare")}</h3>
        <button type="button" className="button" onClick={() => setCreating(true)}>
          {t("orders.new")}
        </button>
        {loading ? <span className="muted">{t("common.loading")}</span> : null}
      </div>
      {error ? <div className="alert error">{error}</div> : null}


      <div
        style={{
          display: "flex",
          gap: "var(--space-2)",
          overflowX: "auto",
          paddingBottom: "var(--space-1)",
        }}
      >
        {days.map(({ date, key }) => {
          const count = (ordersByDay.get(key) ?? []).length;
          const isSelected = key === selectedDayKey;
          return (
            <button
              key={key}
              type="button"
              className={isSelected ? "button" : "button tinted"}
              onClick={() => setSelectedDayKey(key)}
              style={{ flexShrink: 0 }}
            >
              {key === todayKey
                ? t("orders.today")
                : dateText(date, { weekday: "short", day: "numeric" })}
              {count > 0 ? ` (${count})` : ""}
            </button>
          );
        })}
      </div>

      {!selectedIsWorkable ? (
        <p className="muted" style={{ margin: 0 }}>
          {t("orders.previewOnly")}
        </p>
      ) : null}

      {selectedOrders.length === 0 && !loading ? (
        <p className="muted">{t("orders.noneThisDay")}</p>
      ) : (
        selectedOrders.map((order) => (
          <FloorOrderRow
            key={order.id}
            order={order}
            onCollected={setCollected}
            actionable={selectedIsWorkable}
            onChanged={refresh}
            onError={setError}
            onBuildOrder={onBuildOrder}
            onConfirmPickup={confirmPickup}
            confirming={confirmingJobId != null}
          />
        ))
      )}
    </div>
  );
}

function FloorOrderRow({
  order,
  actionable,
  onChanged,
  onError,
  onBuildOrder,
  onConfirmPickup,
  confirming,
  onCollected,
}: {
  order: CustomerOrder;
  actionable: boolean;
  onChanged: () => Promise<void>;
  onError: (message: string | null) => void;
  /** G-156: the sale a counter handover wrote, for the receipt question. */
  onCollected: (sale: CollectedSale) => void;
  onBuildOrder?: (order: CustomerOrder) => void;
  /** G-21 amendment: florist confirms "picked up by courier" on the job. */
  onConfirmPickup?: (jobId: string) => Promise<void>;
  confirming?: boolean;
}) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  // G-20 "Customer collected": open, pick Cash/Card, confirm.
  const [collecting, setCollecting] = useState(false);
  // G-141 (OD-215): nothing on to start, as at the till — Cash and Card
  // toggle, so a Card already on would turn a tap on Cash into a split.
  const [collectPayment, setCollectPayment] = useState<PaymentChoice>(NO_PAYMENT_CHOICE);
  // OD-32: the price actually charged — prefilled with the agreed figure;
  // the customer changing their mind is normal, recorded, never blocked.
  const [collectPriceStr, setCollectPriceStr] = useState("");
  // G-147 (owner 2026-10-13): goods the customer adds at the counter.
  const extras = useTicketCart();
  // G-177 (KI-132): the floors of the order's own bouquets, from the shelf
  // list the till reads — loaded when the handover opens.
  const ownsBouquet =
    order.bouquetBuild != null || order.lines.some((line) => line.role === "READY_BOUQUET");
  const [shelf, setShelf] = useState<FloorBouquetInStock[]>([]);
  useEffect(() => {
    if (!collecting || !ownsBouquet) return;
    let cancelled = false;
    shopModeApi
      .floorListBouquetsInStock()
      .then((list) => {
        if (!cancelled) setShelf(list);
      })
      // A warning only: without the list the counter warns nothing, as before.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [collecting, ownsBouquet]);
  const retailLines = order.lines.filter((line) => line.role === "RETAIL_ITEM");
  // G-15: the server decides whether a bouquet build is still owed
  // (composed lines OR a written/photographed brief) — read its answer so
  // this button can never disagree with the READY gate's 409.
  const bouquetDone = !order.bouquetRequired;
  const retailDone = retailLines.every((line) => Boolean(line.pickedAt));
  const canMarkReady = bouquetDone && retailDone;

  // G-112 (OD-99/101): what is still owed on the goods AND the courier fee.
  // Nothing leaves the shop unpaid, so the rest is recorded here ("Take
  // payment") — paid by phone, Bizum, transfer or in person.
  const owedNow = order.balanceDueEur != null ? Number(order.balanceDueEur) : 0;
  const canTakePayment =
    owedNow > 0 && order.status !== "CANCELLED" && order.status !== "COMPLETED";
  const [paying, setPaying] = useState(false);
  const [payAmountStr, setPayAmountStr] = useState("");
  const [payMethod, setPayMethod] = useState<StaffOrderPaymentMethod>("CARD");
  const payAmount = Number(payAmountStr);
  const payValid =
    payAmountStr.trim() !== "" &&
    Number.isFinite(payAmount) &&
    payAmount > 0 &&
    payAmount <= owedNow + 0.001;

  const takePayment = async () => {
    setBusy(true);
    onError(null);
    try {
      await shopModeApi.floorTakeOrderPayment(order.id, {
        method: payMethod,
        amountEur: Math.round(payAmount * 100) / 100,
      });
      setPaying(false);
      await onChanged();
    } catch (err: unknown) {
      onError(errorText(err, "orders.couldNotTakePayment"));
    } finally {
      setBusy(false);
    }
  };

  // G-112 (OD-103): ticking an item that lives in a storage box asks "Is
  // this from box X?" (the till's own prompt); the counter handover re-asks
  // only if stock moved since.
  const boxPrompt = useBoxPickPrompt();

  const run = async (action: () => Promise<CustomerOrder>) => {
    setBusy(true);
    onError(null);
    try {
      await action();
      await onChanged();
    } catch (err: unknown) {
      onError(errorText(err, "orders.couldNotUpdate"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="day-shifts-row shop-floor-task-card">
      <div className="day-shifts-row-head">
        <div>
          <strong>{orderSlotLabel(t, order)}</strong> · {order.customerName}
        </div>
        <span className={orderStatusClass(order.status)}>
          {/* G-166: a handed-over web order stays here until its courier takes it. */}
          {order.webAwaitingCourier
            ? t("orders.webAwaitingCourier")
            : orderStatusLabel(t, order.status)}
        </span>
      </div>
      <p style={{ margin: 0 }}>
        {orderLinesSummary(order)}
      </p>
      <p className="muted" style={{ margin: 0 }}>
        {fulfillmentLabel(t, order.fulfillment)}
        {/* G-112: the total the customer owes — goods + courier fee. */}
        {order.amountOwedEur ? ` · ${money(order.amountOwedEur)}` : ""}
        {Number(order.paidEur) > 0
          ? ` · ${t("orders.paidAmount", { amount: money(order.paidEur) })}`
          : order.prepaidNotRecorded
            ? ` · ${t("orders.prepaidNotRecorded")}`
            : ""}
        {owedNow > 0 ? ` · ${t("orders.owedNow", { amount: money(owedNow) })}` : ""}
        {order.customerPhone ? ` · ${order.customerPhone}` : ""}
      </p>
      {order.customerNotes ? <p style={{ margin: 0 }}>{order.customerNotes}</p> : null}
      {order.bouquetName ? (
        <p style={{ margin: 0 }}>
          <strong>{t("orders.bouquetLabel")}</strong> {order.bouquetName}
        </p>
      ) : null}
      {order.bouquetNotes ? (
        <p style={{ margin: 0 }}>
          <strong>{t("orders.bouquetNotesLabel")}</strong> {order.bouquetNotes}
        </p>
      ) : null}
      {order.referencePhotoCount > 0 ? (
        <OrderReferencePhotos orderId={order.id} mode="floor" />
      ) : null}

      {retailLines.length > 0 ? (
        <div className="sf-order-pick-list">
          <strong>{t("orders.shopItems")}</strong>
          {retailLines.map((line) => (
            <label key={line.id} className="sf-order-pick-line">
              <input
                type="checkbox"
                checked={Boolean(line.pickedAt)}
                disabled={!actionable || order.status !== "IN_PREPARATION" || busy}
                onChange={(event) => {
                  const picked = event.target.checked;
                  void run(() =>
                    boxPrompt.run((boxAnswers) =>
                      shopModeApi.floorSetCustomerOrderLinePicked(
                        order.id,
                        line.id,
                        picked,
                        boxAnswers,
                      ),
                    ),
                  );
                }}
              />
              <span>
                {Number(line.quantity)} × {line.itemName ?? line.description}
              </span>
              {line.pickedBy ? (
                <small>{t("orders.pickedBy", { name: line.pickedBy.fullName })}</small>
              ) : null}
            </label>
          ))}
        </div>
      ) : null}
      {/* G-112 (OD-99): any day, any stage — money may arrive early. */}
      {canTakePayment ? (
        !paying ? (
          <div className="button-row">
            <button
              type="button"
              className="button tinted"
              onClick={() => {
                setPayAmountStr(owedNow.toFixed(2));
                setPaying(true);
              }}
              disabled={busy}
            >
              {t("orders.takePayment")}
            </button>
          </div>
        ) : (
          <div className="sf-order-collect">
            <div className="field">
              <label htmlFor={`pay-amount-${order.id}`}>
                {t("orders.amountReceived")}
              </label>
              <input
                id={`pay-amount-${order.id}`}
                type="number"
                inputMode="decimal"
                min="0.01"
                max={owedNow.toFixed(2)}
                step="0.01"
                value={payAmountStr}
                onChange={(e) => setPayAmountStr(e.target.value)}
                disabled={busy}
              />
              <small className="hint">{t("orders.takePaymentHint")}</small>
            </div>
            <div className="button-row">
              {(["CASH", "CARD", "BANK_TRANSFER"] as const).map((method) => (
                <button
                  key={method}
                  type="button"
                  className={payMethod === method ? "button" : "button plain"}
                  onClick={() => setPayMethod(method)}
                  disabled={busy}
                >
                  {method === "CASH"
                    ? t("sale.cash")
                    : method === "CARD"
                      ? t("sale.card")
                      : t("sale.transfer")}
                </button>
              ))}
            </div>
            <div className="button-row">
              <button
                type="button"
                className="button"
                onClick={() => void takePayment()}
                disabled={busy || !payValid}
              >
                {busy ? t("tasks.saving") : t("orders.recordPayment")}
              </button>
              <button
                type="button"
                className="button plain"
                onClick={() => setPaying(false)}
                disabled={busy}
              >
                {t("orders.back")}
              </button>
            </div>
          </div>
        )
      ) : null}

      {actionable && order.status === "CONFIRMED" ? (
        <div className="button-row">
          <button
            type="button"
            className="button"
            onClick={() => void run(() => shopModeApi.floorStartOrderPreparation(order.id))}
            disabled={busy}
          >
            {busy ? t("tasks.saving") : t("orders.startPreparation")}
          </button>
        </div>
      ) : null}
      {actionable && order.status === "IN_PREPARATION" ? (
        <div className="button-row sf-order-actions">
          {order.bouquetRequired ? (
            <button
              type="button"
              className="button tinted"
              onClick={() => onBuildOrder?.(order)}
              disabled={busy || !onBuildOrder}
            >
              {t("orders.buildBouquet")}
            </button>
          ) : null}
          {order.bouquetBuild ? (
            <span className="badge success">{t("orders.bouquetBuilt")}</span>
          ) : null}
          <button
            type="button"
            className="button"
            onClick={() => void run(() => shopModeApi.floorMarkOrderReady(order.id))}
            disabled={busy || !canMarkReady}
          >
            {busy ? t("orders.saving") : t("orders.ready")}
          </button>
          {!canMarkReady ? (
            <span className="muted">
              {!bouquetDone && !retailDone
                ? t("orders.buildAndPickFirst")
                : !bouquetDone
                  ? t("orders.buildBouquetFirst")
                  : t("orders.pickItemsFirst")}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* G-21 amendment (OD-28): dual pickup confirmation. The florist marks
          "Picked up by courier" as the shop's witness; if the COURIER claimed
          pickup first and the shop is silent, the card actively asks. */}
      {actionable &&
      order.fulfillment === "COURIER" &&
      order.deliveryJob &&
      !order.deliveryJob.handoverConfirmedAt &&
      // "ACCEPTED" is a timestamp (acceptedAt), never a status — bug_002.
      ["ASSIGNED", "PICKED_UP", "DELIVERED"].includes(
        order.deliveryJob.status,
      ) ? (
        ["PICKED_UP", "DELIVERED"].includes(order.deliveryJob.status) ? (
          <div className="alert" role="status">
            <strong>
              {t("orders.didCourierPickUp", {
                courier:
                  order.deliveryJob.courier?.fullName ?? t("orders.theCourier"),
              })}
            </strong>{" "}
            {t("orders.courierMarkedPickedUp")}
            <div className="button-row" style={{ marginTop: 6 }}>
              {/* Review fix (OD-101): the server refuses this confirmation
                  while money is owed — say so instead of offering a 409.
                  G-166: nor before "Hand to delivery" on a web order. */}
              {order.webAwaitsHandover ? (
                <span className="muted">{t("orders.webHandToDeliveryFirst")}</span>
              ) : owedNow > 0 ? (
                <span className="muted">
                  {t("orders.payBeforeCourier", { amount: money(owedNow) })}
                </span>
              ) : (
                <button
                  type="button"
                  className="button"
                  disabled={confirming}
                  onClick={() => void onConfirmPickup?.(order.deliveryJob!.id)}
                >
                  {confirming ? t("orders.confirming") : t("orders.yesPickedUpByCourier")}
                </button>
              )}
            </div>
          </div>
        ) : order.status === "READY" || order.webAwaitingCourier ? (
          <div className="button-row">
            {/* OD-101: nothing leaves the shop while money is owed — the
                server refuses the handover too; this says why up front.
                G-166: a web order leaves only after "Hand to delivery". */}
            {order.webAwaitsHandover ? (
              <span className="muted">{t("orders.webHandToDeliveryFirst")}</span>
            ) : owedNow > 0 ? (
              <span className="muted">
                {t("orders.payBeforeCourier", { amount: money(owedNow) })}
              </span>
            ) : (
              <button
                type="button"
                className="button tinted"
                disabled={confirming}
                onClick={() => void onConfirmPickup?.(order.deliveryJob!.id)}
              >
                {confirming ? t("orders.confirming") : t("orders.pickedUpByCourier")}
              </button>
            )}
          </div>
        ) : null
      ) : null}

      {/* G-20: the handover — a READY pickup ends HERE, money recorded at the
          moment the customer takes the goods. Courier orders end at delivery.
          OD-32: the price charged is editable — the customer who ordered 100
          roses may take 150; the difference is recorded, never blocked. */}
      {actionable && order.status === "READY" && order.fulfillment === "PICKUP" ? (
        order.webAwaitsHandover ? (
          // G-166: a web order switched to pickup is handed over on the web
          // orders list; the counter's door is refused by the server.
          <p className="muted" style={{ margin: 0 }}>
            {t("orders.webHandToDeliveryFirst")}
          </p>
        ) : !collecting ? (
          <div className="button-row">
            <button
              type="button"
              className="button"
              onClick={() => {
                setCollectPriceStr(order.totalEur ?? "");
                extras.setCart([]);
                setCollecting(true);
              }}
              disabled={busy}
            >
              {t("orders.customerCollected")}
            </button>
          </div>
        ) : (
          (() => {
            // OD-32: a changed price is absorbed by the BOUQUET line, so the
            // editable field only exists when this order HAS a built bouquet
            // (2026-08-12 review: item-only orders were invited to enter a
            // price the server then refused, with no kiosk path left).
            const priceEditable = order.bouquetBuild != null;
            const agreed = order.totalEur != null ? Number(order.totalEur) : null;
            const charged = priceEditable
              ? Number(collectPriceStr)
              : (agreed ?? NaN);
            const priceValid =
              priceEditable
                ? collectPriceStr.trim() !== "" &&
                  Number.isFinite(charged) &&
                  charged >= 0
                : agreed !== null;
            const differs =
              priceEditable &&
              priceValid &&
              (agreed === null || charged !== agreed);
            // G-147: goods added at the counter are owed like the rest.
            const owedCents = priceValid ? Math.round(charged * 100) + extras.totalCents : null;
            const paidCents = Math.round(Number(order.paidEur) * 100);
            const dueNow =
              owedCents === null ? null : Math.max(0, owedCents - paidCents) / 100;
            const owesNow = dueNow !== null && dueNow > 0;
            const dueCents = owesNow ? Math.round(dueNow * 100) : 0;
            const collectReading = readPaymentChoice(collectPayment, dueCents);
            const refundDue =
              owedCents !== null && owedCents < paidCents ? (paidCents - owedCents) / 100 : 0;
            const extrasProblem = ticketLineProblem(extras.cart, t);
            // G-177 (KI-132): warn BEFORE the handover, as the till does.
            const floorWarnings = belowFloorWarnings([
              ...orderBouquetFloorLines(order, differs ? charged : null, shelf),
              ...cartBouquetFloorLines(extras.cart),
            ]);
            if (!priceEditable && agreed === null) {
              return (
                <p className="muted" style={{ margin: 0 }}>
                  {t("orders.noAgreedPriceSetInCrm")}
                </p>
              );
            }
            return (
              <div className="sf-order-collect">
                {priceEditable ? (
                  <div className="field">
                    <label htmlFor={`collect-price-${order.id}`}>
                      {t("inlineSell.priceCharged")}
                    </label>
                    <input
                      id={`collect-price-${order.id}`}
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={collectPriceStr}
                      onChange={(e) => setCollectPriceStr(e.target.value)}
                      disabled={busy}
                    />
                    {differs && agreed !== null ? (
                      <small className="hint">
                        {t("orders.differsFromAgreed", { amount: money(agreed) })}
                      </small>
                    ) : agreed === null ? (
                      <small className="hint">
                        {t("orders.noAgreedPriceEntered")}
                      </small>
                    ) : null}
                  </div>
                ) : null}
                <ShopFloorCollectExtras order={order} ticket={extras} disabled={busy} />
                {extrasProblem ? (
                  <p className="payment-split-problem">{extrasProblem}</p>
                ) : null}
                <strong>
                  {dueNow === null
                    ? t("orders.enterPriceCharged")
                    : dueNow > 0
                      ? Number(order.paidEur) > 0
                        ? t("orders.paidCollect", {
                            paid: money(order.paidEur),
                            due: money(dueNow),
                          })
                        : t("orders.collectAmount", { due: money(dueNow) })
                      : refundDue > 0
                        ? t("orders.paidHandBack", {
                            paid: money(order.paidEur),
                            refund: money(refundDue),
                          })
                        : t("orders.alreadyPaidInFull")}
                </strong>
                {refundDue > 0 ? (
                  <p className="muted" style={{ margin: 0 }}>
                    {t("orders.overpaidRefundInCrm")}
                  </p>
                ) : null}
                {floorWarnings.length > 0 ? (
                  <div className="alert warning below-floor-warning">
                    {floorWarnings.map((w) => (
                      <div key={w}>{w}</div>
                    ))}
                  </div>
                ) : null}
                {owesNow ? (
                  <>
                    <ShopFloorPaymentSplit
                      value={collectPayment}
                      onChange={setCollectPayment}
                      totalCents={dueCents}
                      idPrefix={`collect-${order.id}`}
                      disabled={busy}
                    />
                    {!collectReading.ok && collectReading.problem === "pickMethod" ? (
                      <p className="payment-split-problem">
                        {paymentProblemText(collectReading.problem, dueCents, t)}
                      </p>
                    ) : null}
                  </>
                ) : null}
                <div className="button-row">
                  <button
                    type="button"
                    className="button"
                    onClick={() =>
                      void run(async () => {
                        const completed = await boxPrompt.run((boxAnswers) =>
                          shopModeApi.floorCollectOrder(order.id, {
                            // Nothing owed: no payment to record (G-141).
                            ...(owesNow && collectReading.ok
                              ? { payments: collectReading.parts }
                              : {}),
                            ...(differs ? { chargedTotalEur: charged } : {}),
                            ...(extras.cart.length
                              ? { extraLines: cartToSaleLines(extras.cart) }
                              : {}),
                            ...(boxAnswers.length ? { boxAnswers } : {}),
                          }),
                        );
                        if (completed.sale) {
                          onCollected({
                            saleId: completed.sale.id,
                            belowFloor: completed.belowFloor ?? [],
                          });
                        }
                        return completed;
                      })
                    }
                    disabled={
                      busy ||
                      !priceValid ||
                      extrasProblem !== null ||
                      (owesNow && !collectReading.ok)
                    }
                  >
                    {busy ? t("tasks.saving") : t("orders.confirmHandover")}
                  </button>
                  <button
                    type="button"
                    className="button plain"
                    onClick={() => setCollecting(false)}
                    disabled={busy}
                  >
                    {t("orders.back")}
                  </button>
                </div>
              </div>
            );
          })()
        )
      ) : null}
      {boxPrompt.element}
    </div>
  );
}
